/*
 * Copyright 2026 Sony Corporation
 */

import { RUNNING_STATUSES, type Asset } from '@ips-device-verse/twin-service/schema/assets';

/**
 * How My Library reads a user's assets (3d-reconstruction.md §9).
 *
 * The two outputs of one reconstruction are shown together, under the video
 * they came from. Everything else is a plain tile.
 */

export interface Reconstruction {
  reconstruction_id: string;
  source?: Asset;
  splat?: Asset;
  mesh?: Asset;
  started_at: string;
}

export interface LibraryView {
  reconstructions: Reconstruction[];
  others: Asset[];
}

export const isRunning = (asset: Asset): boolean => RUNNING_STATUSES.includes(asset.status);

/** Newest first, as the service lists them. A video stays a plain tile: it is what Reconstruct is pressed on. */
export const viewOf = (assets: readonly Asset[]): LibraryView => {
  const byId = new Map(assets.map((asset) => [asset.asset_id, asset]));
  const runs = new Map<string, Reconstruction>();
  const others: Asset[] = [];

  for (const asset of assets) {
    const runId = asset.reconstruction_id;
    if (!runId) {
      others.push(asset);
      continue;
    }
    const run = runs.get(runId) ?? {
      reconstruction_id: runId,
      source: asset.source_asset_id ? byId.get(asset.source_asset_id) : undefined,
      started_at: asset.created_at,
    };
    if (asset.kind === 'splat') {
      run.splat = asset;
    } else {
      run.mesh = asset;
    }
    runs.set(runId, run);
  }
  return { reconstructions: [...runs.values()], others };
};

const STAGES: Partial<Record<Asset['status'], string>> = {
  queued: 'Queued',
  preparing: 'Preparing the video',
  training: 'Training the splat',
  meshing: 'Building the mesh',
  finalizing: 'Finishing',
};

const minutes = (since: string, now: number): string => {
  const elapsed = Math.max(0, Math.floor((now - Date.parse(since)) / 60_000));
  return elapsed < 1
    ? 'just now'
    : elapsed < 60
      ? `${elapsed} min`
      : `${Math.floor(elapsed / 60)} h ${elapsed % 60} min`;
};

/**
 * One line under a tile: the stage, what it is waiting on, and for how long.
 *
 * The wait is said out loud. A GPU once took 1 h 47 min to arrive, and a
 * stage name alone over that long reads as a stall.
 */
export const statusLine = (asset: Asset, now: number): string | undefined => {
  if (asset.status === 'failed') {
    return asset.failure ?? 'Failed.';
  }
  if (asset.status === 'uploading') {
    return 'The upload did not finish.';
  }
  const stage = STAGES[asset.status];
  if (!stage) {
    return undefined;
  }
  const job = asset.job;
  if (job?.phase === 'waiting') {
    return `${stage} · waiting for another reconstruction to finish · ${minutes(job.since, now)}`;
  }
  if (job?.phase === 'starting') {
    // `prepare` runs on a CPU instance; only the two later stages wait for a GPU.
    const machine = asset.status === 'preparing' ? 'a machine' : 'a GPU';
    return `${stage} · waiting for ${machine} · ${minutes(job.since, now)}`;
  }
  return `${stage} · ${minutes(job?.since ?? asset.updated_at, now)}`;
};

/** A run's failed outputs, which Retry clears before it starts the video again. */
export const failedOf = (run: Reconstruction): Asset[] =>
  [run.splat, run.mesh].filter((asset): asset is Asset => asset?.status === 'failed');

export const anyRunning = (assets: readonly Asset[]): boolean => assets.some(isRunning);
